import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X, Receipt, ArrowRight, BookOpen } from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";

interface ManualPaymentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectGeneral: () => void;
  onSelectAlbum: () => void;
}

export default function ManualPaymentTypeModal({
  isOpen,
  onClose,
  onSelectGeneral,
  onSelectAlbum,
}: ManualPaymentTypeModalProps) {
  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell
        style={{ maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-payment-type-title"
      >
        <ModalHero style={{ padding: "20px 24px" }}>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <ModalTitle id="manual-payment-type-title" style={{ fontSize: 20 }}>
                Manual Payment
              </ModalTitle>
              <ModalSubtitle>
                Select customer type to record an offline payment.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close modal">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody style={{ padding: "20px 24px 24px" }}>
          <CardsContainer>
            {/* Option 1: New Customer Manual Payment */}
            <OptionCard
              type="button"
              onClick={() => {
                onClose();
                onSelectGeneral();
              }}
            >
              <IconWrapper>
                <Receipt size={20} strokeWidth={2} />
              </IconWrapper>
              <CardContent>
                <CardTitle>New Customer Payment</CardTitle>
                <CardDescription>
                  Record an offline payment (Cash, UPI, Bank Transfer, etc.) for a new customer without an album.
                </CardDescription>
              </CardContent>
              <CardAction>
                <span>Select</span>
                <ArrowRight size={15} />
              </CardAction>
            </OptionCard>

            {/* Option 2: Existing Customer Manual Payment */}
            <OptionCard
              type="button"
              onClick={() => {
                onClose();
                onSelectAlbum();
              }}
            >
              <IconWrapper>
                <BookOpen size={20} strokeWidth={2} />
              </IconWrapper>
              <CardContent>
                <CardTitle>Existing Customer Payment</CardTitle>
                <CardDescription>
                  Record an offline payment for an existing album customer and update album balance.
                </CardDescription>
              </CardContent>
              <CardAction>
                <span>Select</span>
                <ArrowRight size={15} />
              </CardAction>
            </OptionCard>
          </CardsContainer>
        </ModalBody>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
}

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.button`
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 14px;
  padding: 18px 16px;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 14px;
  transition: all 0.18s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

  &:hover {
    border-color: #c94daa;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: none;
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(23, 23, 23, 0.05);
  color: #171717;
  transition: all 0.18s ease;

  ${OptionCard}:hover & {
    background: rgba(201, 77, 170, 0.1);
    color: #c94daa;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
  line-height: 1.25;
`;

const CardDescription = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(23, 23, 23, 0.6);
`;

const CardAction = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.7);
  margin-top: 2px;
  transition: all 0.18s ease;

  ${OptionCard}:hover & {
    color: #c94daa;
    gap: 8px;
  }
`;
